import { createSlice } from '@reduxjs/toolkit';

import { categoriesDirectoryMapperFromApi } from '@/components/AdminPanel/Directory/helpers/categoriesDirectoryMapper';
import { reasonsDirectoryMapperFromApi } from '@/components/AdminPanel/Directory/helpers/reasonsDirectoryMapper';
import { templatesDirectoryMapperFromApi } from '@/components/AdminPanel/Directory/helpers/templatesDirectoryMapper';
import {
  type TCategoryDictionary,
  type TCategoryDictionaryDto
} from '@/components/AdminPanel/Directory/types/categories.types';
import {
  type TReasonDictionary,
  type TReasonDictionaryDto
} from '@/components/AdminPanel/Directory/types/reasons.types';
import {
  type TTemplateDictionary,
  type TTemplateDictionaryDto
} from '@/components/AdminPanel/Directory/types/templates.types';
import { DictTypes } from '@/constants';
import { dictionaryApi } from '@/integrations/adminApi/dictionaryApi';

import { createAsyncThunk } from '../create-async-thunk';
import { getOptimizedDictionaries } from '../dict.slice';

type TDictionarySlice = {
  category: TCategoryDictionary | null;
  categories: TCategoryDictionary[];
  totalCategories: number;
  isLoadingCategories: boolean;

  reason: TReasonDictionary | null;
  reasons: TReasonDictionary[];
  totalReasons: number;
  isLoadingReasons: boolean;

  template: TTemplateDictionary | null;
  templates: TTemplateDictionary[];
  totalTemplates: number;
  isLoadingTemplates: boolean;
};

const initialState: TDictionarySlice = {
  category: null,
  categories: [],
  totalCategories: 0,
  isLoadingCategories: false,

  reason: null,
  reasons: [],
  totalReasons: 0,
  isLoadingReasons: false,

  template: null,
  templates: [],
  totalTemplates: 0,
  isLoadingTemplates: false
};

// ------- Categories -------
export const getCategories = createAsyncThunk(
  'admin/dictionaries/categories',
  async (params: {
    limit: number;
    page: number;
    isActive?: boolean;
    searchTerm?: string;
  }) => {
    return dictionaryApi.getDictCategories({
      limit: params.limit,
      page: params.page,
      // TODO: добавить фильтр по статусу
      // ...(params.isActive !== undefined ? { isActive: params.isActive } : {}),
      ...(params.searchTerm !== undefined
        ? { searchTerm: params.searchTerm }
        : {})
    });
  }
);

export const getCategoryById = createAsyncThunk(
  'admin/categoryId',
  async (categoryId: string) => {
    return dictionaryApi.getDictCategoryById(categoryId);
  }
);

export const createCategory = createAsyncThunk(
  'admin/dictionaries/categories/create',
  async (category: TCategoryDictionaryDto, { dispatch }) => {
    const response = await dictionaryApi.createDictCategory(category);
    await dispatch(
      getOptimizedDictionaries({
        dictTypes: [DictTypes.SupportCategories],
        updatingDicts: [DictTypes.SupportCategories]
      })
    );
    return response;
  }
);

export const updateCategory = createAsyncThunk(
  'admin/dictionaries/categories/update',
  async (category: TCategoryDictionaryDto, { dispatch }) => {
    const response = await dictionaryApi.updateDictCategory(category);
    await dispatch(
      getOptimizedDictionaries({
        dictTypes: [DictTypes.SupportCategories],
        updatingDicts: [DictTypes.SupportCategories]
      })
    );
    return response;
  }
);

// ------- Reasons -------
export const getReasons = createAsyncThunk(
  'admin/dictionaries/reasons',
  async ({
    limit,
    page,
    searchTerm
  }: {
    limit?: number;
    page?: number;
    searchTerm?: string;
  }) => {
    return dictionaryApi.getDictReasons(limit, page, searchTerm);
  }
);

export const getReasonById = createAsyncThunk(
  'admin/reasonId',
  async (reasonId: string) => {
    return dictionaryApi.getDictReasonById(reasonId);
  }
);

export const createReason = createAsyncThunk(
  'admin/dictionaries/reasons/create',
  async (reason: TReasonDictionaryDto, { dispatch }) => {
    const response = await dictionaryApi.createDictReason(reason);
    await dispatch(
      getOptimizedDictionaries({
        dictTypes: [DictTypes.SupportReasons],
        updatingDicts: [DictTypes.SupportReasons]
      })
    );
    return response;
  }
);

export const updateReason = createAsyncThunk(
  'admin/dictionaries/reasons/update',
  async (reason: TReasonDictionaryDto, { dispatch }) => {
    const response = await dictionaryApi.updateDictReason(reason);
    await dispatch(
      getOptimizedDictionaries({
        dictTypes: [DictTypes.SupportReasons],
        updatingDicts: [DictTypes.SupportReasons]
      })
    );
    return response;
  }
);

// ------- Templates -------
export const getTemplates = createAsyncThunk(
  'admin/dictionaries/tempates',
  async ({
    limit,
    page,
    searchTerm
  }: {
    limit?: number;
    page?: number;
    searchTerm?: string;
  }) => {
    return dictionaryApi.getDictTemplates(limit, page, searchTerm);
  }
);

export const getTemplateById = createAsyncThunk(
  'admin/templateId,',
  async (templateId: string) => {
    return dictionaryApi.getDictTemplateById(templateId);
  }
);

export const createTemplate = createAsyncThunk(
  'admin/dictionaries/template/create',
  async (template: TTemplateDictionaryDto) => {
    return dictionaryApi.createDictTemplate(template);
  }
);

export const updateTemplate = createAsyncThunk(
  'admin/dictionaries/template/update',
  async (template: TTemplateDictionaryDto) => {
    return dictionaryApi.updateDictTemplate(template);
  }
);

export const adminDictionarySlice = createSlice({
  name: 'admin/dictionaries',
  initialState: initialState,
  reducers: {
    resetAdminDictionary: () => initialState
  },
  extraReducers: (builder) => {
    builder
      // ------- Categories -------
      .addCase(getCategories.pending, (state) => {
        state.isLoadingCategories = true;
      })
      .addCase(getCategories.fulfilled, (state, action) => {
        state.categories = action.payload.category.map((category) =>
          categoriesDirectoryMapperFromApi(category)
        );
        state.totalCategories = action.payload.total;
        state.isLoadingCategories = false;
      })
      .addCase(getCategories.rejected, (state) => {
        state.isLoadingCategories = false;
      })
      .addCase(getCategoryById.pending, (state) => {
        state.isLoadingCategories = true;
        state.category = null;
      })
      .addCase(getCategoryById.fulfilled, (state, action) => {
        state.isLoadingCategories = false;
        state.category = categoriesDirectoryMapperFromApi(action.payload);
      })
      .addCase(getCategoryById.rejected, (state) => {
        state.isLoadingCategories = false;
      })

      // ------- Reasons -------
      .addCase(getReasons.pending, (state) => {
        state.isLoadingReasons = true;
      })
      .addCase(getReasons.fulfilled, (state, action) => {
        state.reasons = action.payload.reason.map((reason) =>
          reasonsDirectoryMapperFromApi(reason)
        );
        state.totalReasons = action.payload.total;
        state.isLoadingReasons = false;
      })
      .addCase(getReasons.rejected, (state) => {
        state.isLoadingReasons = false;
      })
      .addCase(getReasonById.pending, (state) => {
        state.isLoadingReasons = true;
        state.reason = null;
      })
      .addCase(getReasonById.fulfilled, (state, action) => {
        state.isLoadingReasons = false;
        state.reason = reasonsDirectoryMapperFromApi(action.payload);
      })
      .addCase(getReasonById.rejected, (state) => {
        state.isLoadingReasons = false;
      })

      // ------- Templates -------
      .addCase(getTemplates.pending, (state) => {
        state.isLoadingTemplates = true;
      })
      .addCase(getTemplates.fulfilled, (state, action) => {
        state.templates = action.payload.template.map((template) =>
          templatesDirectoryMapperFromApi(template)
        );
        state.totalTemplates = action.payload.total;
        state.isLoadingTemplates = false;
      })
      .addCase(getTemplates.rejected, (state) => {
        state.isLoadingTemplates = false;
      })
      .addCase(getTemplateById.pending, (state) => {
        state.isLoadingTemplates = true;
        state.template = null;
      })
      .addCase(getTemplateById.fulfilled, (state, action) => {
        state.isLoadingTemplates = false;
        state.template = templatesDirectoryMapperFromApi(action.payload);
      })
      .addCase(getTemplateById.rejected, (state) => {
        state.isLoadingTemplates = false;
      });
  }
});

export const { resetAdminDictionary } = adminDictionarySlice.actions;
